import React, { lazy, Suspense, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import makeStyles from "@material-ui/styles/makeStyles";
import useTheme from "@material-ui/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import localDE from "../../locales/de.json";
import localEN from "../../locales/en.json";

const ReactSVGIcon = lazy(() =>
	import(/* webpackChunkName: "reactSVGIcon" */ "@micado-digital/react-svg-icon/ReactSVGIcon")
);

const useStyles = makeStyles(theme => ({
	root: {
		marginLeft: -16,
		marginRight: -16,
		[theme.breakpoints.up("sm")]: {
			marginLeft: "auto",
			marginRight: "auto"
		}
	},
	rootHighlights: {
		marginLeft: "auto",
		marginRight: "auto",
		[theme.breakpoints.up("md")]: {
			"& .extension-equipment__item:not(:nth-child(3n))": {
				borderRight: "1px solid " + theme.palette.secondary.light
			}
		}
	},
	wrapper: {
		"& button": {
			fontSize: 16,
			marginLeft: "auto",
			marginRight: "auto",
			marginTop: theme.spacing(4.5),
			"& .MuiButton-endIcon": {
				marginLeft: theme.spacing(2),
				marginTop: -2
			},
			"&:hover": {
				background: "none"
			}
		}
	},
	item: {
		alignItems: "center",
		display: "flex",
		marginBottom: 2,
		"& > div": {
			alignItems: "center",
			background: theme.palette.background.teaser.light,
			display: "flex",
			height: "100%",
			justifyContent: "flex-start",
			padding: theme.spacing(3, 2.5) + " !important",
			width: "100%",
			[theme.breakpoints.up("sm")]: {
				borderRadius: 4
			}
		},
		[theme.breakpoints.up("sm")]: {
			marginBottom: 0
		}
	},
	hide: {
		display: "none"
	},
	itemHighlights: {
		"& > div": {
			background: "none",
			borderBottom: "1px solid " + theme.palette.secondary.light,
			padding: theme.spacing(2, 0) + " !important"
		},
		"& .extension-equipment__item-description": {
			fontFamily: "'Marat-Regular'",
			fontWeight: "normal",
			[theme.breakpoints.up("md")]: {
				fontSize: 25
			},
			[theme.breakpoints.up("lg")]: {
				fontSize: 35
			}
		},
		[theme.breakpoints.up("md")]: {
			"& > div": {
				borderBottom: "none",
				flexDirection: "column",
				justifyContent: "center"
			},
			"&:nth-child(n+4) > div": {
				borderTop: "1px solid " + theme.palette.secondary.light,
				paddingTop: "48px !important"
			},
			"& .extension-equipment__item-icon": {
				margin: theme.spacing(0, 0, 2)
			}
		}
	},
	icon: {
		marginRight: theme.spacing(3)
	}
}));

const Equipment = ({ items, lang, variant }) => {
	const css = useStyles();
	const theme = useTheme();
	const [showItems, setShowItems] = useState(false);
	const screenXS = useMediaQuery(theme.breakpoints.only("xs"));
	const screenSM = useMediaQuery(theme.breakpoints.only("sm"));
	const screenMD = useMediaQuery(theme.breakpoints.only("md"));
	const screenLG = useMediaQuery(theme.breakpoints.only("lg"));
	const screenXL = useMediaQuery(theme.breakpoints.only("xl"));
	const bigScreen = useMediaQuery(theme.breakpoints.up("lg"));
	const l = useLocals({
		lang: lang,
		res: {
			de: localDE,
			en: localEN
		}
	});

	const showMore = () => {
		setShowItems(true);
	};

	const options = {
		spacing: function () {
			if (variant !== "highlights") {
				if (screenXS) return 0;
				if (screenSM) return 1;
				if (screenMD) return 2;
				if (screenLG) return 4;
				if (screenXL) return 4;
				return 0;
			} else {
				if (screenXS) return 0;
				if (screenSM) return 3;
				if (screenMD) return 4;
				if (screenLG) return 6;
				if (screenXL) return 6;
			}
		}
	};

	return (
		<div
			className={clsx("extension-equipment", css.root, {
				[css.rootHighlights]: variant === "highlights"
			})}
		>
			<Grid
				className={clsx("extension-equipment__wrapper", css.wrapper)}
				container
				spacing={options.spacing()}
			>
				{items?.map(({ id, textcontent }, i) => {
					let description = null;
					let icon = null;

					textcontent?.items?.filter(item => {
						if (item.name === "description") {
							return (description = item?.text);
						}

						if (item.name === "icon") {
							return (icon = item?.text);
						}

						return null;
					});

					return (
						<Grid
							key={id}
							className={clsx("extension-equipment__item", css.item, {
								[css.hide]: i > 5 && showItems === false,
								[css.itemHighlights]: variant === "highlights"
							})}
							item
							container
							xs={12}
							sm={6}
							md={4}
						>
							<div>
								{icon && (
									<Suspense fallback={<></>}>
										<ReactSVGIcon
											className={clsx("extension-equipment__item-icon", css.icon)}
											color="initial"
											src={"/img/room/" + icon + ".svg"}
											size={bigScreen ? 56 : 32}
										/>
									</Suspense>
								)}
								{description && (
									<Typography
										className={clsx("extension-equipment__item-description", css.description)}
										component="p"
										dangerouslySetInnerHTML={{ __html: description }}
										variant="subtitle1"
									/>
								)}
							</div>
						</Grid>
					);
				})}
				{items && items?.length > 6 && showItems === false && (
					<Button
						color="primary"
						variant="text"
						size="small"
						onClick={showMore}
						endIcon={
							<Suspense fallback={<></>}>
								<ReactSVGIcon color="primary" src={"/img/icons/plus.svg"} size={14} />
							</Suspense>
						}
					>
						{l("equipment.button")}
					</Button>
				)}
			</Grid>
		</div>
	);
};

Equipment.propTypes = {
	items: PropTypes.array.isRequired
};

export default Equipment;
